import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { AggregatedInboundRow, ServiceTypeKey, SettlementStatisticDetailItem } from '@/services/settlement/settlementManagement/types'
import { COLUMN_WIDTH } from '@/utils/grid/grid'
import { Utils } from '@/utils/Utils'
import { AmountCell } from '@/pages/settlement/SettlementManagement/components'
import { isNil } from 'lodash-es'
import { HeaderTooltip } from './HeaderTooltip'
import Text from '@/components/content/Text'
import { colorSystem } from '@/styles/theme'

function formatWon(value?: number) {
  if (isNil(value)) return '-'
  return `${Utils.getCommaNum(value)}원`
}

// LP9182 — per-cost 탭(보관/고객출고/매장출고/반품) 공통 국가 컬럼. line-item country(EnumResult) .name 표시. 단가관리(#442/#450) valueGetter 동일 패턴
const countryColumn: ColDef = {
  colId: 'country',
  headerName: '국가',
  width: 90,
  valueGetter: ({ data }) => data?.country?.name ?? '-',
}

// LP9182 — 입고비 탭 국가 = 항상 "한국"(BE InboundSettlementStrategy 가드, 일본/중국 필터 시 빈 결과). 집계 row(AggregatedInboundRow)엔 country 필드가 없어 상수로 표시
const inboundCountryColumn: ColDef = {
  colId: 'country',
  headerName: '국가',
  width: 90,
  valueGetter: () => '한국',
}

const commonColumns: ColDef[] = [
  {
    field: 'brandName',
    headerName: '브랜드명(브랜드ID)',
    minWidth: COLUMN_WIDTH.status,
    valueFormatter: ({ value, data }) => {
      return value ? `${value}(${data?.brandId})` : '-'
    },
  },
  { field: 'productNumber', headerName: '상품번호', minWidth: COLUMN_WIDTH.status, valueFormatter: ({ value }) => value || '-' },
  { field: 'productName', headerName: '상품명', minWidth: 240, valueFormatter: ({ value }) => value || '-' },
  { field: 'productOption', headerName: '옵션', minWidth: COLUMN_WIDTH.status, valueFormatter: ({ value }) => value || '-' },
  { field: 'productBarcode', headerName: '바코드', minWidth: COLUMN_WIDTH.status, valueFormatter: ({ value }) => value || '-' },
]

// LOGISPRD-14189 — sku_id 노출 flag ON 에서 commonColumns 를 쓰는 탭에 바코드 바로 뒤로 끼운다
const skuIdColumn: ColDef = { field: 'skuId', headerName: 'SKU ID', minWidth: COLUMN_WIDTH.status, valueFormatter: ({ value }) => value || '-' }

// LOGISPRD-14189 — flag ON 에서 상품번호·옵션을 숨기는 탭. 고객출고·반품은 주문 단위라 유지한다
const SKU_VIEW_HIDE_PRODUCT_TABS: ServiceTypeKey[] = ['INVENTORY', 'INBOUND', 'OUTBOUND_TO_STORE']
const SKU_VIEW_TABS: ServiceTypeKey[] = [...SKU_VIEW_HIDE_PRODUCT_TABS, 'OUTBOUND_TO_CUSTOMER', 'RETURN_INBOUND']

const withSkuIdColumn = <T,>(columnDefs: ColDef<T>[], hideProductNumberAndOption: boolean): ColDef<T>[] =>
  columnDefs.flatMap((col) => {
    const field = col.field as string | undefined
    if (hideProductNumberAndOption && (field === 'productNumber' || field === 'productOption')) return []
    return field === 'productBarcode' ? [col, skuIdColumn as ColDef<T>] : [col]
  })

// LP9182 — 전체(ALL) 탭은 일자별 국가집합(countries CSV)을 code→name 매핑해야 해서 renderCountries(컴포넌트의 useGetCountries 기반)를 주입받는 함수형으로 둔다
const dailySettlementColumnDefs = (renderCountries: (csv?: string | null) => string): ColDef<SettlementStatisticDetailItem<'ALL'>>[] => [
  {
    field: 'settlementDate',
    headerName: '정산일자',
    minWidth: COLUMN_WIDTH.time,
    pinned: true,
    valueFormatter: ({ value }) => value || '-',
  },
  {
    field: 'totalAmount.originAmount',
    headerName: '일별 정산금액',
    minWidth: COLUMN_WIDTH.status,
    pinned: true,
    autoHeight: true,
    headerComponent: () => <HeaderTooltip headerName="일별 정산금액" content="정산금액 : A+B+C+D+E" />,
    cellRenderer: ({ value, data }: ICellRendererParams<SettlementStatisticDetailItem<'ALL'>>) => (
      <AmountCell originAmount={value} discountedAmount={data?.totalAmount?.discountedAmount} />
    ),
  },
  {
    // LP9182 — 일자별 국가집합. 필터연동: 전체→"한국,일본"/일본→"일본"/데이터 없는 일자→'-'
    colId: 'countries',
    headerName: '국가',
    width: COLUMN_WIDTH.status,
    valueGetter: ({ data }) => renderCountries(data?.countries),
  },
  {
    field: 'inventoryAmount.originAmount',
    headerName: '보관비(A)',
    width: COLUMN_WIDTH.status,
    autoHeight: true,
    headerClass: 'ag-right-aligned-header',
    cellRenderer: ({ value, data }: ICellRendererParams<SettlementStatisticDetailItem<'ALL'>>) => (
      <AmountCell originAmount={value} discountedAmount={data?.inventoryAmount?.discountedAmount} />
    ),
  },
  {
    field: 'inboundAmount.originAmount',
    headerName: '입고비(B)',
    width: COLUMN_WIDTH.status,
    autoHeight: true,
    headerClass: 'ag-right-aligned-header',
    cellRenderer: ({ value, data }: ICellRendererParams<SettlementStatisticDetailItem<'ALL'>>) => (
      <AmountCell originAmount={value} discountedAmount={data?.inboundAmount?.discountedAmount} />
    ),
  },
  {
    field: 'customerOutboundAmount.originAmount',
    headerName: '고객출고비(C)',
    width: COLUMN_WIDTH.status,
    autoHeight: true,
    headerClass: 'ag-right-aligned-header',
    cellRenderer: ({ value, data }: ICellRendererParams<SettlementStatisticDetailItem<'ALL'>>) => (
      <AmountCell originAmount={value} discountedAmount={data?.customerOutboundAmount?.discountedAmount} />
    ),
  },
  {
    field: 'storeOutboundAmount.originAmount',
    headerName: '매장출고비(D)',
    width: COLUMN_WIDTH.status,
    autoHeight: true,
    headerClass: 'ag-right-aligned-header',
    cellRenderer: ({ value, data }: ICellRendererParams<SettlementStatisticDetailItem<'ALL'>>) => (
      <AmountCell originAmount={value} discountedAmount={data?.storeOutboundAmount.discountedAmount} />
    ),
  },
  {
    field: 'returnInboundAmount.originAmount',
    headerName: '반품비(E)',
    width: COLUMN_WIDTH.status,
    autoHeight: true,
    headerClass: 'ag-right-aligned-header',
    cellRenderer: ({ value, data }: ICellRendererParams<SettlementStatisticDetailItem<'ALL'>>) => (
      <AmountCell originAmount={value} discountedAmount={data?.returnInboundAmount?.discountedAmount} />
    ),
  },
  // {
  //   field: 'releaseAmount.originAmount',
  //   headerName: '반출비(E)',
  //   width: COLUMN_WIDTH.status,
  //   autoHeight: true,
  //   headerClass: 'ag-right-aligned-header',
  //   cellRenderer: ({ value, data }: ICellRendererParams<SettlementStatisticDetailItem<'ALL'>>) => (
  //     <AmountCell originAmount={value} discountedAmount={data?.releaseAmount?.discountedAmount} />
  //   ),
  // },
  {
    field: 'promotionDiscountAmount',
    headerName: '프로모션 할인',
    width: COLUMN_WIDTH.status,
    headerClass: ['ag-right-aligned-header', 'no-divider'],
    cellClass: 'ag-right-aligned-cell',
    valueFormatter: ({ value }) => formatWon(value),
  },
]

const inventoryColumnDefs: ColDef<SettlementStatisticDetailItem<'INVENTORY'>>[] = [
  {
    field: 'settlementDate',
    headerName: '보관일자',
    minWidth: COLUMN_WIDTH.time,
    pinned: true,
    valueFormatter: ({ value }) => value || '-',
  },
  {
    field: 'totalAmount.discountedAmount',
    headerName: '보관비',
    minWidth: COLUMN_WIDTH.status,
    pinned: true,
    cellClass: 'ag-right-aligned-cell',
    headerComponent: () => <HeaderTooltip headerName="보관비" content="보관비 : 보관수량 x 보관단가" />,
    valueFormatter: ({ value }) => formatWon(value),
  },
  countryColumn,
  ...commonColumns,
  { field: 'logisticsCategory', headerName: '물류 카테고리', minWidth: 220, valueFormatter: ({ value }) => value || '-' },
  {
    field: 'quantity',
    headerName: '보관수량(PCS)',
    minWidth: COLUMN_WIDTH.time,
    valueFormatter: ({ value }) => (value ? Utils.getCommaNum(value) : '-'),
  },
  {
    field: 'unitPrice',
    headerName: '보관단가',
    minWidth: COLUMN_WIDTH.time,
    autoHeight: true,
    headerClass: ['ag-right-aligned-header', 'no-divider'],
    cellRenderer: ({ value, data }: ICellRendererParams<SettlementStatisticDetailItem<'INVENTORY'>>) => (
      <AmountCell originAmount={value} discountedAmount={data?.discountedUnitPrice} />
    ),
  },
]

// LP8238 박스단위 입고 정산 — 집계 표시 row(AggregatedInboundRow) 기준 컬럼.
// 순서: 총수량 → PCS 수량 → BOX 수량 → 입수량 → PCS 단가 → BOX 단가 (PRD 정책 + BE Excel order 9~16, unit 대문자 라벨 2026-06-02 결정)
const inboundColumnDefs: ColDef<AggregatedInboundRow>[] = [
  {
    field: 'settlementDate',
    headerName: '입고일자',
    minWidth: COLUMN_WIDTH.time,
    pinned: true,
    valueFormatter: ({ value }) => value || '-',
  },
  {
    field: 'totalAmount.discountedAmount',
    headerName: '입고비',
    minWidth: COLUMN_WIDTH.status,
    pinned: true,
    cellClass: 'ag-right-aligned-cell',
    headerComponent: () => <HeaderTooltip headerName="입고비" content="입고비 : (PCS 수량 × PCS 단가) + (BOX 수량 × BOX 단가)" />,
    valueFormatter: ({ value }) => formatWon(value),
  },
  inboundCountryColumn,
  ...commonColumns,
  {
    field: 'totalQuantity',
    headerName: '총수량',
    minWidth: COLUMN_WIDTH.time,
    // 0 은 유효 수량이므로 '-' 가 아닌 '0' 으로 표시 (undefined/null 만 '-')
    valueFormatter: ({ value }) => (isNil(value) ? '-' : Utils.getCommaNum(value)),
  },
  {
    field: 'pcsQuantity',
    headerName: 'PCS 수량',
    minWidth: COLUMN_WIDTH.time,
    valueFormatter: ({ value }) => (isNil(value) ? '-' : Utils.getCommaNum(value)),
  },
  {
    field: 'boxQuantity',
    headerName: 'BOX 수량',
    minWidth: COLUMN_WIDTH.time,
    valueFormatter: ({ value }) => (isNil(value) ? '-' : Utils.getCommaNum(value)),
  },
  {
    field: 'pcsPerUnit',
    headerName: '입수량',
    minWidth: COLUMN_WIDTH.time,
    valueFormatter: ({ value }) => (isNil(value) ? '-' : Utils.getCommaNum(value)),
  },
  {
    // field 없이 valueGetter 를 쓰므로 colId 를 명시해 컬럼 상태/엑셀 export 순서가 인덱스 기반 ID 에 의존하지 않도록 한다
    colId: 'pcsUnitPrice',
    headerName: 'PCS 단가',
    // 정렬값은 숫자(할인전 originAmount). field 에 Amount 객체를 바인딩하면 AG Grid 가 객체를 비교해 정렬이 깨진다 (다른 금액 컬럼과 동일하게 숫자 기준)
    valueGetter: ({ data }) => data?.pcsUnitPrice?.originAmount,
    minWidth: COLUMN_WIDTH.time,
    autoHeight: true,
    headerClass: ['ag-right-aligned-header', 'no-divider'],
    cellRenderer: ({ data }: ICellRendererParams<AggregatedInboundRow>) => (
      <AmountCell originAmount={data?.pcsUnitPrice?.originAmount} discountedAmount={data?.pcsUnitPrice?.discountedAmount} />
    ),
  },
  {
    colId: 'boxUnitPrice',
    headerName: 'BOX 단가',
    // 정렬값은 숫자(할인전 originAmount). field 에 Amount 객체를 바인딩하면 정렬이 깨진다
    valueGetter: ({ data }) => data?.boxUnitPrice?.originAmount,
    minWidth: COLUMN_WIDTH.time,
    autoHeight: true,
    headerClass: ['ag-right-aligned-header', 'no-divider'],
    cellRenderer: ({ data }: ICellRendererParams<AggregatedInboundRow>) => (
      <AmountCell originAmount={data?.boxUnitPrice?.originAmount} discountedAmount={data?.boxUnitPrice?.discountedAmount} />
    ),
  },
]

const outBoundColumnDefs = (cutoverApplied: boolean): ColDef<SettlementStatisticDetailItem<'OUTBOUND_TO_CUSTOMER'>>[] => [
  {
    field: 'settlementDate',
    headerName: cutoverApplied ? '출고일자' : '구매확정일자',
    width: COLUMN_WIDTH.time,
    pinned: true,
    valueFormatter: ({ value }) => value || '-',
  },
  {
    field: 'totalAmount.discountedAmount',
    headerName: '고객출고비',
    width: COLUMN_WIDTH.time,
    pinned: true,
    cellClass: 'ag-right-aligned-cell',
    headerComponent: () => <HeaderTooltip headerName="고객출고비" content="고객출고비 : 출고작업비 + 출고택배비 + 도서산간비" />,
    valueFormatter: ({ value }) => formatWon(value),
  },
  countryColumn,
  { field: 'orderNumber', headerName: '주문번호', width: 160, valueFormatter: ({ value }) => value || '-' },
  { field: 'orderOptionNumber', headerName: '주문일련번호', width: 120, valueFormatter: ({ value }) => value || '-' },
  ...commonColumns,
  { field: 'invoiceNumber', headerName: '송장번호', width: COLUMN_WIDTH.status, valueFormatter: ({ value }) => value || '-' },
  {
    field: 'quantity',
    headerName: '출고수량(PCS)',
    width: COLUMN_WIDTH.status,
    valueFormatter: ({ value }) => (value ? Utils.getCommaNum(value) : '-'),
  },
  {
    field: 'unitPrice',
    headerName: '출고단가',
    width: COLUMN_WIDTH.status,
    autoHeight: true,
    headerClass: 'ag-right-aligned-header',
    cellRenderer: ({ value, data }: ICellRendererParams<SettlementStatisticDetailItem<'OUTBOUND_TO_CUSTOMER'>>) => (
      <AmountCell originAmount={value} discountedAmount={data?.discountedUnitPrice} />
    ),
  },
  {
    field: 'customerOutboundAmount.originAmount',
    headerName: '출고작업비',
    width: COLUMN_WIDTH.status,
    cellClass: 'ag-right-aligned-cell',
    headerComponent: () => <HeaderTooltip headerName="출고작업비" content="출고작업비 : 출고수량 x 출고단가" />,
    cellRenderer: ({ value, data }: ICellRendererParams<SettlementStatisticDetailItem<'OUTBOUND_TO_CUSTOMER'>>) => (
      <AmountCell originAmount={value} discountedAmount={data?.outboundAmount.discountedAmount} />
    ),
  },
  {
    field: 'outboundShippingAmount.originAmount',
    headerName: '출고택배비',
    width: COLUMN_WIDTH.status,
    headerClass: 'ag-right-aligned-header',
    cellClass: 'ag-right-aligned-cell',
    cellRenderer: ({ value, data }: ICellRendererParams<SettlementStatisticDetailItem<'OUTBOUND_TO_CUSTOMER'>>) => (
      <AmountCell originAmount={value} discountedAmount={data?.outboundShippingAmount.discountedAmount} />
    ),
  },
  {
    field: 'remoteAreaShippingAmount',
    headerName: '도서산간비',
    width: COLUMN_WIDTH.time,
    headerClass: ['ag-right-aligned-header', 'no-divider'],
    cellClass: 'ag-right-aligned-cell',
    valueFormatter: ({ value }) => formatWon(value),
  },
]

const storeOutboundColumnDefs: ColDef<SettlementStatisticDetailItem<'OUTBOUND_TO_STORE'>>[] = [
  {
    field: 'settlementDate',
    headerName: '출고일자',
    width: COLUMN_WIDTH.time,
    pinned: true,
    valueFormatter: ({ value }) => value || '-',
  },
  {
    field: 'totalAmount.discountedAmount',
    headerName: '매장출고비',
    width: COLUMN_WIDTH.time,
    pinned: true,
    cellClass: 'ag-right-aligned-cell',
    headerComponent: () => <HeaderTooltip headerName="매장출고비" content="매장출고비 : 출고작업비 + 출고운송비" />,
    valueFormatter: ({ value }) => formatWon(value),
  },
  countryColumn,
  ...commonColumns,
  {
    field: 'quantity',
    headerName: '출고수량(PCS)',
    width: COLUMN_WIDTH.status,
    valueFormatter: ({ value }) => (value ? Utils.getCommaNum(value) : '-'),
  },
  {
    field: 'unitPrice',
    headerName: '출고단가',
    width: COLUMN_WIDTH.status,
    autoHeight: true,
    headerClass: 'ag-right-aligned-header',
    cellRenderer: ({ value, data }: ICellRendererParams<SettlementStatisticDetailItem<'OUTBOUND_TO_STORE'>>) => (
      <AmountCell originAmount={value} discountedAmount={data?.discountedUnitPrice} />
    ),
  },
  {
    field: 'customerOutboundAmount.originAmount',
    headerName: '출고작업비',
    width: 120,
    autoHeight: true,
    headerClass: 'ag-right-aligned-header',
    headerComponent: () => <HeaderTooltip headerName="출고작업비" content="출고작업비 : 출고수량 X 출고단가" />,
    cellRenderer: ({ value, data }: ICellRendererParams<SettlementStatisticDetailItem<'OUTBOUND_TO_STORE'>>) => (
      <AmountCell originAmount={value} discountedAmount={data?.outboundAmount?.discountedAmount} />
    ),
  },
  {
    field: 'outboundShippingAmount.originAmount',
    headerName: '출고운송비',
    width: 120,
    autoHeight: true,
    headerClass: ['ag-right-aligned-header', 'no-divider'],
    headerComponent: () => <HeaderTooltip headerName="출고운송비" content="출고운송비 : 출고수량 X 156원" position="right" />,
    cellRenderer: ({ value, data }: ICellRendererParams<SettlementStatisticDetailItem<'OUTBOUND_TO_STORE'>>) => (
      <AmountCell originAmount={value} discountedAmount={data?.outboundShippingAmount?.discountedAmount} />
    ),
  },
]

const returnInboundColumnDefs = (cutoverApplied: boolean): ColDef<SettlementStatisticDetailItem<'RETURN_INBOUND'>>[] => [
  {
    field: 'settlementDate',
    headerName: cutoverApplied ? '입고일자' : '클레임완료일자',
    width: COLUMN_WIDTH.time,
    pinned: true,
    valueFormatter: ({ value }) => value || '-',
  },
  {
    field: 'totalAmount.discountedAmount',
    headerName: '반품비',
    width: COLUMN_WIDTH.time,
    pinned: true,
    headerComponent: () => (
      <HeaderTooltip
        headerName="반품비"
        content="반품비 : 반품작업비+반품택배비+반품도서산간비-(출고작업비 환불+출고택배비 환불+도서산간비 환불)"
      />
    ),
    cellClass: 'ag-right-aligned-cell',
    valueFormatter: ({ value }) => formatWon(value),
  },
  countryColumn,
  { field: 'originOrderNumber', headerName: '원주문번호', width: 160, valueFormatter: ({ value }) => value || '-' },
  { field: 'originOrderOptionNumber', headerName: '원주문일련번호', width: 120, valueFormatter: ({ value }) => value || '-' },
  ...commonColumns,
  { field: 'invoiceNumber', headerName: '반품 송장번호', width: COLUMN_WIDTH.status, valueFormatter: ({ value }) => value || '-' },
  { field: 'responsibleParty.name', headerName: '귀책주체', width: COLUMN_WIDTH.status, valueFormatter: ({ value }) => value || '-' },
  {
    field: 'isFreeReturn',
    headerName: '고객무료반품',
    width: COLUMN_WIDTH.status,
    cellDataType: 'yn',
    headerComponent: () => (
      <HeaderTooltip
        headerName="고객무료반품"
        content={
          <Text kind="Body_12_medium" color={colorSystem.text.common.inverse} style={{ whiteSpace: 'pre' }}>
            {`고객무료반품 대상건은 고객 귀책일지라도\n파트너 귀책과 동일하게 비용이 청구됩니다.`}
          </Text>
        }
      />
    ),
    valueFormatter: ({ value }) => (value === true ? 'Y' : value === false ? 'N' : '-'),
  },
  {
    field: 'quantity',
    headerName: '반품수량(PCS)',
    width: COLUMN_WIDTH.status,
    valueFormatter: ({ value }) => (value ? Utils.getCommaNum(value) : '-'),
  },
  {
    field: 'unitPrice',
    headerName: '반품단가',
    width: COLUMN_WIDTH.status,
    autoHeight: true,
    headerClass: 'ag-right-aligned-header',
    cellRenderer: ({ value, data }: ICellRendererParams<SettlementStatisticDetailItem<'RETURN_INBOUND'>>) => (
      <AmountCell originAmount={value} discountedAmount={data?.discountedUnitPrice} />
    ),
  },
  {
    field: 'returnInboundAmount.originAmount',
    headerName: '반품작업비',
    width: COLUMN_WIDTH.status,
    headerComponent: () => <HeaderTooltip headerName="반품작업비" content="반품작업비 : 반품수량 x 반품단가" />,
    cellClass: 'ag-right-aligned-cell',
    autoHeight: true,
    cellRenderer: ({ value, data }: ICellRendererParams<SettlementStatisticDetailItem<'RETURN_INBOUND'>>) => (
      <AmountCell originAmount={value} discountedAmount={data?.returnInboundAmount.discountedAmount} />
    ),
  },
  {
    field: 'returnShippingAmount.originAmount',
    headerName: '반품택배비',
    width: COLUMN_WIDTH.status,
    headerClass: 'ag-right-aligned-header',
    cellClass: 'ag-right-aligned-cell',
    autoHeight: true,
    cellRenderer: ({ value, data }: ICellRendererParams<SettlementStatisticDetailItem<'RETURN_INBOUND'>>) => (
      <AmountCell originAmount={value} discountedAmount={data?.returnShippingAmount.discountedAmount} />
    ),
  },
  {
    field: 'returnRemoteAreaShippingAmount',
    headerName: '반품도서산간비',
    width: COLUMN_WIDTH.status,
    headerClass: 'ag-right-aligned-header',
    cellClass: 'ag-right-aligned-cell',
    valueFormatter: ({ value }) => formatWon(value),
  },
  {
    field: 'outboundRefundAmount',
    headerName: '출고작업비 환불',
    width: COLUMN_WIDTH.status,
    headerClass: 'ag-right-aligned-header',
    cellClass: 'ag-right-aligned-cell',
    valueFormatter: ({ value }) => formatWon(value),
  },
  {
    field: 'outboundShippingRefundAmount',
    headerName: '출고택배비 환불',
    width: COLUMN_WIDTH.status,
    headerClass: 'ag-right-aligned-header',
    cellClass: 'ag-right-aligned-cell',
    valueFormatter: ({ value }) => formatWon(value),
  },
  {
    field: 'outboundRemoteAreaShippingRefundAmount',
    headerName: '도서산간비 환불',
    width: COLUMN_WIDTH.time,
    headerClass: 'no-divider',
    cellClass: 'ag-right-aligned-cell',
    headerComponent: () => (
      <HeaderTooltip
        headerName="도서산간비 환불"
        position="right"
        content={
          <Text kind="Body_12_medium" color={colorSystem.text.common.inverse} style={{ whiteSpace: 'pre' }}>
            {`무신사 귀책 : 도서산간비 환불\n파트너 귀책 : 도서산간비 청구`}
          </Text>
        }
      />
    ),
    valueFormatter: ({ value }) => formatWon(value),
  },
]

const adjustmentColumnDefs: ColDef<SettlementStatisticDetailItem<'ETC_ADJUSTMENT'>>[] = [
  {
    field: 'createdDate',
    headerName: '등록일',
    width: COLUMN_WIDTH.time,
    pinned: true,
    valueFormatter: ({ value }) => value || '-',
  },
  {
    field: 'adjustmentReasonType.name',
    headerName: '조정항목',
    width: COLUMN_WIDTH.time,
    pinned: true,
    valueFormatter: ({ value }) => value || '-',
  },
  {
    field: 'adjustmentChargeType.name',
    headerName: '조정유형',
    width: COLUMN_WIDTH.status,
    pinned: true,
    valueFormatter: ({ value }) => value || '-',
  },
  {
    field: 'adjustmentAmount',
    headerName: '기타조정비',
    width: COLUMN_WIDTH.status,
    pinned: true,
    headerClass: 'ag-right-aligned-header',
    cellClass: 'ag-right-aligned-cell',
    valueFormatter: ({ value }) => formatWon(value),
  },
  {
    field: 'adjustmentCount',
    headerName: '발생건수',
    width: COLUMN_WIDTH.status,
    valueFormatter: ({ value }) => (value ? Utils.getCommaNum(value) : '-'),
  },
  { field: 'adjustmentReason', headerName: '상세사유', width: COLUMN_WIDTH.status, valueFormatter: ({ value }) => value || '-' },
]

// LOGISPRD-16794 실측비 탭. 옵션·바코드는 보이지 않고 SKU ID 는 14189 flag 와 무관하게 항상 보인다 (SG 결정 2026-10-30)
// 실측비는 프로모션 비대상이라 할인 전후 금액이 같아 단가를 단일 금액으로 보인다. 국가는 입고와 같이 항상 한국
const measurementColumnDefs: ColDef<SettlementStatisticDetailItem<'MEASUREMENT'>>[] = [
  {
    field: 'settlementDate',
    headerName: '실측일자',
    minWidth: COLUMN_WIDTH.time,
    pinned: true,
    valueFormatter: ({ value }) => value || '-',
  },
  {
    field: 'totalAmount.discountedAmount',
    headerName: '실측비',
    minWidth: COLUMN_WIDTH.status,
    pinned: true,
    cellClass: 'ag-right-aligned-cell',
    headerComponent: () => <HeaderTooltip headerName="실측비" content="실측비 : 실측건수 × 실측단가" />,
    valueFormatter: ({ value }) => formatWon(value),
  },
  inboundCountryColumn,
  {
    field: 'brandName',
    headerName: '브랜드명(브랜드ID)',
    minWidth: COLUMN_WIDTH.status,
    valueFormatter: ({ value, data }) => (value ? `${value}(${data?.brandId})` : '-'),
  },
  skuIdColumn,
  { field: 'productNumber', headerName: '상품번호', minWidth: COLUMN_WIDTH.status, valueFormatter: ({ value }) => value || '-' },
  { field: 'productName', headerName: '상품명', minWidth: 240, valueFormatter: ({ value }) => value || '-' },
  {
    field: 'quantity',
    headerName: '실측건수',
    minWidth: COLUMN_WIDTH.time,
    valueFormatter: ({ value }) => (value ? Utils.getCommaNum(value) : '-'),
  },
  {
    field: 'unitPrice',
    headerName: '실측단가',
    minWidth: COLUMN_WIDTH.time,
    headerClass: ['ag-right-aligned-header', 'no-divider'],
    cellClass: 'ag-right-aligned-cell',
    valueFormatter: ({ value }) => formatWon(value),
  },
]

// LP8519: feature.inboundSettlementUnitTypeEnabled 가 OFF 일 때 노출되는 레거시 INBOUND 컬럼 (박스단위 도입 전 원본 — 입고수량(PCS)/입고단가).
// ON 이면 위 inboundColumnDefs(총수량/PCS·BOX 수량/입수량/PCS·BOX 단가, 집계 row) 가 대신 사용된다.
const legacyInboundColumnDefs: ColDef<SettlementStatisticDetailItem<'INBOUND'>>[] = [
  {
    field: 'settlementDate',
    headerName: '입고일자',
    minWidth: COLUMN_WIDTH.time,
    pinned: true,
    valueFormatter: ({ value }) => value || '-',
  },
  {
    field: 'totalAmount.discountedAmount',
    headerName: '입고비',
    minWidth: COLUMN_WIDTH.status,
    pinned: true,
    cellClass: 'ag-right-aligned-cell',
    headerComponent: () => <HeaderTooltip headerName="입고비" content="입고비 : 입고수량 x 입고단가" />,
    valueFormatter: ({ value }) => formatWon(value),
  },
  inboundCountryColumn,
  ...commonColumns,
  {
    field: 'quantity',
    headerName: '입고수량(PCS)',
    minWidth: COLUMN_WIDTH.time,
    valueFormatter: ({ value }) => (value ? Utils.getCommaNum(value) : '-'),
  },
  {
    field: 'unitPrice',
    headerName: '입고단가',
    minWidth: COLUMN_WIDTH.time,
    autoHeight: true,
    headerClass: ['ag-right-aligned-header', 'no-divider'],
    cellRenderer: ({ value, data }: ICellRendererParams<SettlementStatisticDetailItem<'INBOUND'>>) => (
      <AmountCell originAmount={value} discountedAmount={data?.discountedUnitPrice} />
    ),
  },
]

export const getColumnDefs = <Tab extends ServiceTypeKey>(
  currentTab: Tab,
  cutoverApplied: boolean = false,
  // LP8519: 박스단위 입고 정산 feature flag. OFF 면 레거시 INBOUND 컬럼을 노출 (fail-safe 기본 false)
  isInboundSettlementUnitTypeEnabled: boolean = false,
  // LP9182: 전체(ALL) 탭 국가집합(countries CSV) → code→name 변환기. 컴포넌트의 useGetCountries 기반. 미주입 시 '-'
  renderCountries: (csv?: string | null) => string = () => '-',
  // LP9182: 국가 차원 노출 flag. OFF 면 국가 컬럼(colId 'country'/'countries')을 제거해 레거시 동일 (fail-safe 기본 false)
  isCategoryGlobalSettlementEnabled: boolean = false,
  // LOGISPRD-14189: sku_id 노출 flag. OFF 면 SKU ID 컬럼 없이 상품번호·옵션 유지 (fail-safe 기본 false)
  isSkuViewEnabled: boolean = false,
): ColDef<SettlementStatisticDetailItem<Tab>>[] => {
  const columnDefMap = {
    ALL: dailySettlementColumnDefs(renderCountries),
    INVENTORY: inventoryColumnDefs,
    INBOUND: isInboundSettlementUnitTypeEnabled ? inboundColumnDefs : legacyInboundColumnDefs,
    OUTBOUND_TO_CUSTOMER: outBoundColumnDefs(cutoverApplied),
    OUTBOUND_TO_STORE: storeOutboundColumnDefs,
    RETURN_INBOUND: returnInboundColumnDefs(cutoverApplied),
    MEASUREMENT: measurementColumnDefs,
    ETC_ADJUSTMENT: adjustmentColumnDefs,
  } as Record<ServiceTypeKey, ColDef<SettlementStatisticDetailItem<Tab>>[]>

  const columnDefs = columnDefMap[currentTab]
  // LP9182 flag OFF → 국가 컬럼 제거(레거시 동일)
  const visibleColumnDefs = isCategoryGlobalSettlementEnabled
    ? columnDefs
    : columnDefs.filter((col) => col.colId !== 'country' && col.colId !== 'countries')
  if (!isSkuViewEnabled || !SKU_VIEW_TABS.includes(currentTab)) return visibleColumnDefs
  return withSkuIdColumn(visibleColumnDefs, SKU_VIEW_HIDE_PRODUCT_TABS.includes(currentTab))
}
